import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Sparkles, GraduationCap, Heart, FileText, Trash2, Upload, ArrowRight, ArrowLeft } from "lucide-react";

const DOC_TYPES = [
  { value: "iep", label: "IEP" },
  { value: "progress_report", label: "Progress report" },
  { value: "report_card", label: "Report card" },
  { value: "evaluation", label: "Evaluation" },
  { value: "other", label: "Other" },
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();

  const [role, setRole] = useState("teacher");
  const [step, setStep] = useState(0); // for parent: 0=account, 1=student, 2=docs

  // Account
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // Student (parent only)
  const [student, setStudent] = useState({
    first_name: "",
    last_name: "",
    date_of_birth: "",
    grade: "",
    school: "",
    home_address: "",
  });

  // Files
  const [pendingFiles, setPendingFiles] = useState([]); // {file, doc_type}
  const fileRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);

  const addFiles = (fileList) => {
    const arr = Array.from(fileList || []);
    setPendingFiles((p) => [...p, ...arr.map((f) => ({ file: f, doc_type: "other" }))]);
  };

  const onSubmit = async () => {
    if (role === "parent" && (!student.first_name || !student.last_name || !student.grade)) {
      toast.error("Student first name, last name, and grade are required.");
      setStep(1);
      return;
    }
    setSubmitting(true);
    const payload = role === "teacher"
      ? { role }
      : {
          role,
          student_first_name: student.first_name,
          student_last_name: student.last_name,
          student_date_of_birth: student.date_of_birth || null,
          student_grade: student.grade,
          student_school: student.school || null,
          student_home_address: student.home_address || null,
        };
    const res = await register(email, password, name, payload);
    if (!res.ok) {
      setSubmitting(false);
      toast.error(res.error);
      return;
    }
    // Upload documents (parent only)
    if (role === "parent" && pendingFiles.length > 0) {
      let uploaded = 0;
      for (const item of pendingFiles) {
        const fd = new FormData();
        fd.append("file", item.file);
        try {
          await api.post(
            `/documents/upload?doc_type=${encodeURIComponent(item.doc_type)}`,
            fd,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
          uploaded += 1;
        } catch (e) {
          toast.error(`Could not upload ${item.file.name}`);
        }
      }
      if (uploaded > 0) toast.success(`Uploaded ${uploaded} document(s)`);
    }
    setSubmitting(false);
    toast.success("Account created");
    navigate(role === "parent" ? "/parent" : "/");
  };

  const canAdvanceFromAccount = name.trim() && email.trim() && password.length >= 6;

  return (
    <div className="min-h-screen flex items-center justify-center paper p-6">
      <div className="w-full max-w-2xl bg-white border border-[#E8E9E6] rounded-2xl p-8 sm:p-10 shadow-sm">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-xl bg-[#6E5AA0] flex items-center justify-center">
            <Sparkles className="text-white" size={18} />
          </div>
          <span className="font-heading text-xl font-semibold ink">Ellis Path</span>
        </div>

        <h2 className="font-heading text-3xl font-medium ink">Create your account</h2>
        <p className="ink-muted mt-2 text-sm">Plan, track, and care — all in one place.</p>

        {/* Role selector always visible */}
        <div className="mt-6 space-y-2">
          <Label>I am a</Label>
          <div className="grid grid-cols-2 gap-3">
            <RoleCard active={role === "teacher"} onClick={() => { setRole("teacher"); setStep(0); }} icon={GraduationCap} title="Teacher" subtitle="Special education teacher" testid="role-teacher" />
            <RoleCard active={role === "parent"} onClick={() => { setRole("parent"); setStep(0); }} icon={Heart} title="Parent" subtitle="Caregiver of a student" testid="role-parent" />
          </div>
        </div>

        {/* Parent stepper */}
        {role === "parent" && (
          <div className="mt-6 flex items-center gap-2 flex-wrap">
            {["Your account", "Student info", "Documents"].map((s, i) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${i <= step ? "bg-[#6E5AA0] text-white" : "bg-[#F0EBF6] text-[#6B726F]"}`}>{i + 1}</div>
                <span className={`text-sm ${i === step ? "ink font-medium" : "ink-muted"}`}>{s}</span>
                {i < 2 && <div className="w-6 h-px bg-[#E8E9E6] mx-1" />}
              </div>
            ))}
          </div>
        )}

        {/* Step 0 — account (and full form for teacher) */}
        {step === 0 && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (role === "teacher") onSubmit();
              else if (canAdvanceFromAccount) setStep(1);
            }}
            className="mt-6 space-y-4"
            data-testid="register-form"
          >
            <div className="space-y-2">
              <Label htmlFor="name">Your name</Label>
              <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} placeholder={role === "teacher" ? "Ms. Garcia" : "Sam Rivera"} className="h-11" data-testid="register-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@school.edu" className="h-11" data-testid="register-email-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" className="h-11" data-testid="register-password-input" />
            </div>

            <div className="pt-2 flex justify-end">
              {role === "teacher" ? (
                <Button type="submit" disabled={submitting} className="bg-[#6E5AA0] hover:bg-[#574785] text-white h-11 px-6" data-testid="register-submit-button">
                  {submitting ? "Creating account…" : "Create account"}
                </Button>
              ) : (
                <Button type="submit" disabled={!canAdvanceFromAccount} className="bg-[#6E5AA0] hover:bg-[#574785] text-white h-11 px-6" data-testid="register-next-account">
                  Continue <ArrowRight size={14} className="ml-2" />
                </Button>
              )}
            </div>
          </form>
        )}

        {/* Step 1 — student info */}
        {role === "parent" && step === 1 && (
          <div className="mt-6 space-y-4" data-testid="register-student-step">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Student first name</Label>
                <Input value={student.first_name} onChange={(e) => setStudent({ ...student, first_name: e.target.value })} data-testid="student-first-name" />
              </div>
              <div className="space-y-2">
                <Label>Student last name</Label>
                <Input value={student.last_name} onChange={(e) => setStudent({ ...student, last_name: e.target.value })} data-testid="student-last-name" />
              </div>
              <div className="space-y-2">
                <Label>Date of birth</Label>
                <Input type="date" value={student.date_of_birth} onChange={(e) => setStudent({ ...student, date_of_birth: e.target.value })} data-testid="student-dob" />
              </div>
              <div className="space-y-2">
                <Label>Grade</Label>
                <Input value={student.grade} onChange={(e) => setStudent({ ...student, grade: e.target.value })} placeholder="e.g., 3" data-testid="student-grade" />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>School</Label>
                <Input value={student.school} onChange={(e) => setStudent({ ...student, school: e.target.value })} placeholder="Lincoln Elementary" data-testid="student-school" />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Home address</Label>
                <Textarea rows={2} value={student.home_address} onChange={(e) => setStudent({ ...student, home_address: e.target.value })} data-testid="student-address" />
              </div>
            </div>

            <div className="pt-2 flex justify-between">
              <Button type="button" variant="ghost" onClick={() => setStep(0)} data-testid="register-back-1">
                <ArrowLeft size={14} className="mr-2" /> Back
              </Button>
              <Button type="button" onClick={() => setStep(2)} disabled={!student.first_name || !student.last_name || !student.grade} className="bg-[#6E5AA0] hover:bg-[#574785] text-white h-11 px-6" data-testid="register-next-student">
                Continue <ArrowRight size={14} className="ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2 — documents */}
        {role === "parent" && step === 2 && (
          <div className="mt-6 space-y-4" data-testid="register-docs-step">
            <p className="text-sm ink-muted">
              Optionally upload supporting documents (IEPs, progress reports, report cards, evaluations, etc.). You can also add more after signing in.
            </p>

            <div className="border-2 border-dashed border-[#D8D9D5] rounded-2xl p-6 text-center bg-[#FBFBF9]">
              <Upload className="mx-auto text-[#6E5AA0]" size={26} />
              <p className="ink mt-2 font-medium">Add files</p>
              <p className="ink-muted text-xs mt-1">PDF · DOCX · JPG · PNG (max 25 MB each)</p>
              <input
                ref={fileRef}
                type="file"
                multiple
                accept=".pdf,.docx,.doc,.jpg,.jpeg,.png,.webp,.txt"
                className="hidden"
                onChange={(e) => { addFiles(e.target.files); if (fileRef.current) fileRef.current.value = ""; }}
                data-testid="register-files-input"
              />
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="mt-3" data-testid="register-files-pick">
                Choose files
              </Button>
            </div>

            {pendingFiles.length > 0 && (
              <div className="space-y-2">
                {pendingFiles.map((p, i) => (
                  <Card key={i} className="p-3 border border-[#E8E9E6]" data-testid={`pending-file-${i}`}>
                    <div className="flex items-center gap-3">
                      <FileText size={16} className="text-[#6E5AA0] shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium ink truncate">{p.file.name}</div>
                        <div className="text-xs ink-muted">{Math.round(p.file.size / 1024)} KB</div>
                      </div>
                      <Select value={p.doc_type} onValueChange={(v) => setPendingFiles((arr) => arr.map((x, j) => j === i ? { ...x, doc_type: v } : x))}>
                        <SelectTrigger className="w-44 h-9" data-testid={`pending-file-type-${i}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DOC_TYPES.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setPendingFiles((arr) => arr.filter((_, j) => j !== i))} className="text-[#BA6356]">
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}

            <div className="pt-2 flex justify-between">
              <Button type="button" variant="ghost" onClick={() => setStep(1)} data-testid="register-back-2">
                <ArrowLeft size={14} className="mr-2" /> Back
              </Button>
              <Button type="button" onClick={onSubmit} disabled={submitting} className="bg-[#6E5AA0] hover:bg-[#574785] text-white h-11 px-6" data-testid="register-submit-button">
                {submitting ? "Creating account…" : "Create account"}
              </Button>
            </div>
          </div>
        )}

        <div className="mt-6 text-sm ink-muted">
          Already have an account?{" "}
          <Link to="/login" className="text-[#6E5AA0] font-medium hover:underline" data-testid="link-login">
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}

function RoleCard({ active, onClick, icon: Icon, title, subtitle, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-left rounded-xl border p-3 transition-all ${active ? "border-[#6E5AA0] bg-[#EFEAF5] ring-2 ring-[#6E5AA0]/30" : "border-[#E8E9E6] bg-white hover:bg-[#F0EBF6]"}`}
    >
      <div className="flex items-center gap-2">
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${active ? "bg-[#6E5AA0] text-white" : "bg-[#F0EBF6] text-[#6E5AA0]"}`}>
          <Icon size={14} />
        </div>
        <div className="font-medium ink text-sm">{title}</div>
      </div>
      <div className="text-xs ink-muted mt-1.5">{subtitle}</div>
    </button>
  );
}
